import { getRelatedContent } from '../../../api/research-service-api';
import { RelatedContentItem } from '../../../api/types/research-related-content-response';
import { IResearchPrompt } from './ResearchArticlesPrompt';
import { ITrackingPayload } from '../../../types/analytics';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../utilities/analytics';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useEffect, useState, useRef } from 'react';
import { Swiper } from 'swiper/react';
import { Keyboard, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';

import { KmxContainer } from '../../../components/KmxContainer';
import { styled, useTheme } from '@mui/material/styles';
import { CarouselSkipLink } from '../../../components/CarouselSkipLink';
import useAppState from '../../../common/state/appState';
import { ResearchArticlesHeader, UFTResearchArticlesHeader } from './ResearchArticlesHeader';
import { useMuiMediaQuery } from '../../../utilities/hooks/useMuiMediaQuery';
import { ResearchArticlesSlides, UFTResearchArticlesSlides } from './ResearchArticlesSlides';

const StyledSwiper = styled(Swiper)`
    height: 100%;
    width: calc(100% + 16px);
    .swiper-slide {
        width: auto !important;
        display: flex;
        align-items: center;
    }
`;

interface IResearchArticlesContainerProps {
    fullStoryPageId: string;
}

const researchPromptData: IResearchPrompt = {
    type: 'PROMPT',
    heading: 'Visit research',
    buttonLabel: 'view all research',
};

const SLIDES_OFFSET_BEFORE_MD = 24; // Spacing 3
const SPACING_LG = 64;

const ResearchArticlesContainer = ({ fullStoryPageId }: IResearchArticlesContainerProps) => {
    const [researchRelatedContentItems, setResearchRelatedContentItems] = useState<RelatedContentItem[]>([]);
    const serveUFTContent = useAppState((state) => state.serveUFTContent);
    const swiperRef = useRef<SwiperType | null>(null);

    const carouselTitle = serveUFTContent ? 'CarMax experts say' : 'Used car research & advice';

    useEffect(() => {
        getRelatedContent().then((response) => {
            let items: RelatedContentItem[] = response?.data[0]?.content || [];
            items = items.filter((item) => (item as RelatedContentItem)?.image?.url);
            setResearchRelatedContentItems(items);
        });
    }, []);

    // Update navigation when buttons are available or rerendered
    useEffect(() => {
        if (swiperRef.current) {
            const prevButton = document.querySelector('#research-articles-carousel__prev');
            const nextButton = document.querySelector('#research-articles-carousel__next');

            if (prevButton && nextButton) {
                const swiper = swiperRef.current;
                if (swiper.params && swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
                    swiper.params.navigation.prevEl = prevButton as HTMLElement;
                    swiper.params.navigation.nextEl = nextButton as HTMLElement;
                    swiper.navigation.init();
                    swiper.navigation.update();
                }
            }
        }
    }, [researchRelatedContentItems, serveUFTContent]);
    const {
        breakpoints: { values: breakpoints },
    } = useTheme();

    const { isMobile } = useMuiMediaQuery();

    if (researchRelatedContentItems.length < 1) {
        return <></>;
    }

    return (
        <Box
            component="section"
            id="research-articles-component"
            sx={{ pt: { xs: 8, md: 12 }, overflow: 'hidden' }}
            data-fs-section={`${fullStoryPageId}: Research Articles`}
        >
            <KmxContainer maxWidth="xl">
                <Stack direction="column" spacing={{ xs: 2, md: 4 }}>
                    {serveUFTContent ? (
                        <UFTResearchArticlesHeader carouselTitle={carouselTitle} />
                    ) : (
                        <ResearchArticlesHeader carouselTitle={carouselTitle} />
                    )}
                    <CarouselSkipLink
                        id="pre-research-articles-carousel"
                        href="#the-carmax-difference"
                        text="Skip Research Articles Carousel"
                    />
                    <StyledSwiper
                        tabIndex={0}
                        spaceBetween={isMobile && serveUFTContent ? 24 : 12}
                        slidesPerView="auto"
                        slidesPerGroup={1}
                        slidesPerGroupAuto
                        speed={500}
                        style={{ overflow: 'visible' }}
                        breakpoints={{
                            [breakpoints.md]: {
                                spaceBetween: SPACING_LG,
                                slidesOffsetBefore: SLIDES_OFFSET_BEFORE_MD,
                            },
                        }}
                        grabCursor
                        keyboard
                        navigation={{
                            nextEl: '#research-articles-carousel__next',
                            prevEl: '#research-articles-carousel__prev',
                        }}
                        modules={[Navigation, Keyboard]}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        onSlideChange={() => {
                            const name = 'Research & Articles Slide View';
                            const event = ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA;
                            const payload: ITrackingPayload = {
                                event: event,
                                linkDetails: {
                                    name: name,
                                },
                            };
                            trackEDDL(payload);
                        }}
                    >
                        {serveUFTContent
                            ? UFTResearchArticlesSlides({
                                  isMobile,
                                  researchRelatedContentItems,
                              })
                            : ResearchArticlesSlides({
                                  researchRelatedContentItems,
                                  researchPromptData,
                              })}
                    </StyledSwiper>
                </Stack>
            </KmxContainer>
        </Box>
    );
};

export default ResearchArticlesContainer;
